<template>
  <b-list-group>
    <b-list-group-item v-if="showHeader" class="px-2 py-1 suggestion-header font-weight-bold">
      <b-row align-v="center">
        <b-col cols="1">
          <img :src="mapImg" :alt="mapName" class="small-icon mr-2" />
        </b-col>
        <b-col> Filter by location </b-col>
      </b-row>
    </b-list-group-item>
    <LocationSuggestionItem
      v-for="suggestion in locationSuggestions"
      :key="suggestion.osmId"
      :location="suggestion"
      @click="filterByLocation($event)"
    />
    <SuggestionNone v-if="nothingFound && !loading" />
    <b-spinner v-if="loading" class="mx-auto my-1" />
    <small v-if="showHeader"
      >Location Search by <a href="https://locationiq.com/?ref=link-back">LocationIQ.com</a></small
    >
  </b-list-group>
</template>

<script lang="ts">
import Vue from 'vue'
import Component from 'vue-class-component'

import LocationSuggestionItem from '@/components/public/Suggestions/LocationSuggestionItem.vue'
import SuggestionNone from '@/components/public/Suggestions/SuggestionNone.vue'
import { from } from 'rxjs'
import { pluck, filter, debounceTime, distinctUntilChanged, switchMap, map } from 'rxjs/operators'

import mapDefault from '@/assets/imgs/map.svg'
import { getLocationSuggestions, LatLonParams } from '@/api'
import { NavButtonKeys } from '@/types'
import { useNavButtonsStore } from '@/store/public/navButtons'

const components = {
  LocationSuggestionItem,
  SuggestionNone,
}

const props = {
  search: {
    type: String,
    default: '',
  },
}

@Component({ components })
export default class LocationSuggestionList extends Vue.extend({ props }) {
  nothingFound = false
  loading = false
  navButtonsStore = useNavButtonsStore()

  subscriptions() {
    return {
      results: this.$watchAsObservable('search').pipe(
        // get newValue from input/search
        pluck('newValue'),
        // only get those greater than 3 letters or zero for cancel
        filter((text) => text.length >= 3 || text.length === 0),
        // delay the input so that it doesn't fetch on each key pressed
        debounceTime(600),
        // It's like filter, but just emits the values that are distinct from the previous query.
        distinctUntilChanged(),
        // make sure we only emit the results from the most recently placed GET request
        // takes in a callback that returns an observable
        switchMap(this.fetchTerm),
        // The .map() operator applies a function to each event from the source observable,
        // and returns a stream of transformed events
        map(this.formatResult),
      ),
    }
  }

  get mapNavButton() {
    return this.navButtonsStore.allBtns[NavButtonKeys.MAP]
  }

  get mapName() {
    return this.mapNavButton.label
  }

  get mapImg() {
    return this.mapNavButton.iconImageSrc || mapDefault
  }

  get locationSuggestions() {
    return this.$typedStore.public.search.locationResults
  }

  set locationSuggestions(suggestions) {
    this.$typedStore.public.search.setLocationResults(suggestions)
  }

  get showHeader() {
    return this.locationSuggestions.length > 0 || this.nothingFound || this.loading
  }

  fetchTerm(input: string) {
    this.loading = true
    if (this.validate(input)) {
      return from(getLocationSuggestions(input))
    } else {
      return from(Promise.resolve({ cancel: true }))
    }
  }

  formatResult(response: any) {
    this.loading = false
    if (response.cancel === true) {
      this.nothingFound = false
      this.locationSuggestions = []
    } else if (response.length > 0) {
      this.nothingFound = false
      this.locationSuggestions = response
    } else {
      this.nothingFound = true
      this.locationSuggestions = []
    }
  }

  validate(value: unknown) {
    if (typeof value === 'string') {
      return this.required(value.trim())
    }
    return this.required(value)
  }

  required(value: unknown) {
    if (value === undefined || value === null) {
      return false
    }
    if (value === false) {
      return true
    }
    return !!String(value).length
  }

  async filterByLocation(location: LatLonParams) {
    this.$typedStore.public.search.filterByLocation(location)
    this.$analytics.event('click', {
      event_category: 'search-location',
      event_label: `[latitude: ${location.lat}, longitude: ${location.lon}]`,
    })
    this.$bvModal.hide('searchModal')
  }
}
</script>

<style scoped>
.suggestion-header {
  background-color: #d1d1d1;
}

.small-icon {
  width: 2rem;
  font-size: 2rem;
}
</style>
